import { computeStoreOpeningStatus } from './StoreOpeningLogic.js';

/**
 * Calcul pur (sans DOM) des filtres visibles de « Nos revendeurs » (chantier
 * store-locator-revendeurs, point 6, critère 3, scénarios 13-23).
 *
 * Fichier volontairement séparé du rendu (`StoreFilterBar.js`) et du reste de
 * la carte/liste, sur le même principe que `StoreDistance.js` et
 * `StoreOpeningLogic.js` : testable directement en Node, sans navigateur ni
 * build du thème.
 *
 * Règle (critère 3) :
 * - plusieurs types cochés s'additionnent (OU) — aucun type coché = tous les
 *   types ;
 * - « Ouvert maintenant » est une condition supplémentaire (ET) ;
 * - chaque service coché est une condition supplémentaire (ET) — la maquette
 *   de Robin les combine en OU, **non repris** (spec, section « Rendu
 *   visuel ») : chaque service reste une condition ET distincte.
 * - aucun filtre actif du tout = tous les magasins (critère 3).
 */

/** Slugs de la taxonomie `type`, dans l'ordre d'affichage des puces (critère 3). */
export const STORE_TYPE_SLUGS = ['concept-store', 'corner-store', 'revendeur'];

/** Clés ACF des services, dans l'ordre d'affichage des puces (critère 3). */
export const STORE_SERVICE_KEYS = ['custom_frame', 'standard_frame', 'online_print'];

/**
 * Un magasin est « ouvert maintenant » seulement s'il a une semaine reconnue
 * (invariant du critère 9 : jamais de statut deviné) et si le statut calculé
 * à `now` n'est pas fermé (« Ouvert » et « Ferme bientôt » comptent tous les
 * deux comme ouverts).
 * @param  {object} item Magasin (forme de `$features`, `opening_hours_week` éventuel).
 * @param  {Date} now
 * @returns {boolean}
 */
export function isStoreOpenNow(item, now = new Date()) {
  const week = item?.opening_hours_week && Object.keys(item.opening_hours_week).length > 0
    ? item.opening_hours_week
    : null;
  if (!week) {
    return false;
  }
  const status = computeStoreOpeningStatus(week, now);
  return Boolean(status) && status.state !== 'closed';
}

/**
 * @typedef {object} StoreFilters
 * @property {string[]} [types] Slugs de type cochés (OU entre eux ; vide = tous).
 * @property {boolean} [openNow] « Ouvert maintenant » (ET).
 * @property {string[]} [services] Clés de service cochées (chacune une condition ET).
 */

/** @returns {StoreFilters} Filtres vides (aucun filtre actif). */
export function emptyStoreFilters() {
  return { types: [], openNow: false, services: [] };
}

/**
 * Aucun filtre actif du tout (critère 3 : dans ce cas, tous les magasins).
 * @param  {StoreFilters} filters
 * @returns {boolean}
 */
export function isStoreFiltersEmpty(filters) {
  return !filters
    || ((!filters.types || filters.types.length === 0)
      && !filters.openNow
      && (!filters.services || filters.services.length === 0));
}

/**
 * @param  {object} item
 * @param  {StoreFilters} filters
 * @param  {Date} now
 * @returns {boolean}
 */
export function matchesStoreFilters(item, filters, now = new Date()) {
  const { types = [], openNow = false, services = [] } = filters || {};

  if (types.length > 0 && !types.includes(item.type)) {
    return false;
  }
  if (openNow && !isStoreOpenNow(item, now)) {
    return false;
  }
  if (services.length > 0 && !services.every((key) => Boolean(item[key]))) {
    return false;
  }
  return true;
}

/**
 * @param  {Array<object>} items
 * @param  {StoreFilters} filters
 * @param  {Date} now
 * @returns {Array<object>}
 */
export function applyStoreFilters(items, filters, now = new Date()) {
  if (isStoreFiltersEmpty(filters)) {
    return items;
  }
  return items.filter((item) => matchesStoreFilters(item, filters, now));
}
